// EtiquetasXL dynamicFields - S47.2 / S47.3a / S48.1 / S48.2a / S48.2b.
// Helpers + resolvers. El catalogo DYNAMIC_FIELDS vive en
// dynamicFieldsCatalog.js (split S48.2a por limite 300 LOC).
//
// S48.2a agrega resolvers para:
//   producto.nombre_corto    -> truncado 40 chars + word boundary + '...'
//   producto.texto_legal     -> string fijo
//   producto.descripcion     -> texto del producto (compatibilidad)
//   producto.logos           -> resolverLogos + logosToText (config marcas)
//
// S48.2b agrega resolvers para:
//   bundle.descripcion       -> alias de bundle.items_resumen
//   bundle.codigo_barras     -> bundle.codigo_barras || bundle.code
//   bundle.texto_legal       -> string fijo (mismo que producto.texto_legal)
//   bundle.logos             -> resolverLogosFromText + logosToText sobre
//                               bundle.items_resumen (substring word-boundary)
import { DYNAMIC_FIELDS } from './dynamicFieldsCatalog';
import { resolverLogos, logosToText } from './autolayout/logos';
import { resolverLogosFromText } from './autolayout/logos_text';
import { logosMarcas } from './config/logosMarcas';

export { DYNAMIC_FIELDS };

const NOMBRE_CORTO_MAX = 40;
const TEXTO_LEGAL_FIJO = 'ASSEMBLED IN THE U.S. OF DOMESTIC AND IMPORTED PARTS';

export function findDynamicField(key) {
  for (let i = 0; i < DYNAMIC_FIELDS.length; i += 1) {
    if (DYNAMIC_FIELDS[i].key === key) return DYNAMIC_FIELDS[i];
  }
  return null;
}

export function findDynamicByBindKey(bindKey) {
  if (!bindKey) return null;
  for (let i = 0; i < DYNAMIC_FIELDS.length; i += 1) {
    if (DYNAMIC_FIELDS[i].bindKey === bindKey) return DYNAMIC_FIELDS[i];
  }
  return null;
}

export function demoForField(field) {
  if (!field) return '';
  if (field.bindKey) {
    const meta = findDynamicByBindKey(field.bindKey);
    if (meta) return meta.demo;
  }
  if (field.type === 'price') return '$ 0,00';
  if (field.type === 'sku') return 'SKU';
  if (field.type === 'qr') return 'QR';
  if (field.type === 'barcode') return 'BARCODE';
  return 'Texto';
}

// ---------------------------------------------------------------------------
// Resolvers bindKey -> valor real
// ---------------------------------------------------------------------------

const _COP = new Intl.NumberFormat('es-CO', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function _fmtPrecio(n) {
  if (n === null || n === undefined || n === '') return '';
  const v = Number(n);
  if (Number.isNaN(v)) return '';
  return '$ ' + _COP.format(v);
}

function _fmtFechaImpresion(d) {
  const dt = (d instanceof Date) ? d : new Date();
  if (Number.isNaN(dt.getTime())) return '';
  const dd = String(dt.getDate()).padStart(2, '0');
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const yy = String(dt.getFullYear());
  return dd + '/' + mm + '/' + yy;
}

function _truncarNombreCorto(nombre) {
  const s = String(nombre || '').trim();
  if (s.length === 0) return null;
  if (s.length <= NOMBRE_CORTO_MAX) return s;
  let cut = s.lastIndexOf(' ', NOMBRE_CORTO_MAX);
  if (cut < 10) cut = NOMBRE_CORTO_MAX;
  return s.slice(0, cut).trim() + '...';
}

function _resolveProducto(bindKey, producto) {
  if (bindKey === 'producto.fecha' || bindKey === 'producto.fecha_impresion') {
    return _fmtFechaImpresion(new Date());
  }
  if (bindKey === 'producto.lote') return null;
  if (bindKey === 'producto.texto_legal') return TEXTO_LEGAL_FIJO;
  if (!producto || typeof producto !== 'object') return null;
  if (bindKey === 'producto.nombre') {
    const v = producto.nombre;
    return (v === undefined || v === null || v === '') ? null : String(v);
  }
  if (bindKey === 'producto.nombre_corto') {
    return _truncarNombreCorto(producto.nombre);
  }
  if (bindKey === 'producto.descripcion') {
    const v = producto.descripcion;
    return (v === undefined || v === null || v === '') ? null : String(v);
  }
  if (bindKey === 'producto.sku'
      || bindKey === 'producto.codigo_barras'
      || bindKey === 'producto.qr') {
    const v = producto.sku || producto.codigo || null;
    return v ? String(v) : null;
  }
  if (bindKey === 'producto.precio') {
    return _fmtPrecio(producto.precio_venta);
  }
  if (bindKey === 'producto.logos') {
    const arr = resolverLogos(producto.marca, logosMarcas);
    const txt = logosToText(arr);
    return txt || null;
  }
  return null;
}

function _resolveBundle(bindKey, bundle) {
  if (bindKey === 'bundle.texto_legal') return TEXTO_LEGAL_FIJO;
  if (!bundle || typeof bundle !== 'object') return null;
  if (bindKey === 'bundle.code') {
    return bundle.code ? String(bundle.code) : null;
  }
  if (bindKey === 'bundle.name') {
    return bundle.name ? String(bundle.name) : null;
  }
  if (bindKey === 'bundle.sale_price') {
    return _fmtPrecio(bundle.sale_price);
  }
  if (bindKey === 'bundle.items_resumen') {
    return bundle.items_resumen ? String(bundle.items_resumen) : null;
  }
  if (bindKey === 'bundle.descripcion') {
    return bundle.items_resumen ? String(bundle.items_resumen) : null;
  }
  if (bindKey === 'bundle.codigo_barras') {
    const v = bundle.codigo_barras || bundle.code || null;
    return v ? String(v) : null;
  }
  if (bindKey === 'bundle.qr') {
    const v = bundle.code || null;
    return v ? String(v) : null;
  }
  if (bindKey === 'bundle.logos') {
    const arr = resolverLogosFromText(bundle.items_resumen, logosMarcas);
    const txt = logosToText(arr);
    return txt || null;
  }
  return null;
}

export function resolveBindValue(bindKey, producto, bundle) {
  if (!bindKey) return null;
  if (bindKey.indexOf('producto.') === 0) return _resolveProducto(bindKey, producto);
  if (bindKey.indexOf('bundle.') === 0) return _resolveBundle(bindKey, bundle);
  return null;
}

export function valueForField(field, producto, bundle) {
  if (!field) return '';
  if (field.bindKey) {
    const real = resolveBindValue(field.bindKey, producto, bundle);
    if (real !== null && real !== undefined && real !== '') return real;
  }
  return demoForField(field);
}
